import { Appearance, loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { Redirect } from 'react-router-dom'
import {
  PaymentPlan,
  useOnboardingContext,
} from 'context/OnboardingContext/OnboardingProvider'
import CheckoutForm from './CheckoutForm'
import { RoutePaths } from 'containers/Core/Routes'
import OnboardingContainer from '../OnboardingContainer'
import {
  PromoCodeProvider,
  usePromoCodeContext,
} from 'context/PromoCodeContext/PromoCodeProvider'
import usePayment from 'hooks/usePayment/usePayment'
import './styles.scss'
import UseOnboardingPageView from 'hooks/useOnboardingPageView'
import { useEffect, useState } from 'react'
import { ChakraProvider } from '@chakra-ui/react'

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
)

interface PaymentProps {
  isDirectFlow?: boolean
}

const Payment: React.FC<PaymentProps> = ({ isDirectFlow }) => {
  const { selectedPlan } = useOnboardingContext()
  const { trackPageView } = UseOnboardingPageView()
  const { promoCode } = usePromoCodeContext()
  const { createSetupIntent } = usePayment()
  const [error, setError] = useState<string | undefined>(undefined)
  const [clientSecret, setClientSecret] = useState<string | null>(null)

  // Track page view
  useEffect(() => {
    trackPageView('payment')
  }, [])

  useEffect(() => {
    const initializeSetup = async () => {
      try {
        const { clientSecret } = await createSetupIntent()
        setClientSecret(clientSecret)
      } catch (error) {
        console.error('Error initializing setup:', error)
        setError('Unable to initialize payment form. Please try again.')
      }
    }

    if (!clientSecret) {
      initializeSetup()
    }
  }, [])

  if (!selectedPlan) {
    return (
      <Redirect
        to={`${
          isDirectFlow
            ? RoutePaths.DIRECT_PAYMENT_PLANS
            : RoutePaths.PAYMENT_PLANS
        }${window.location.search}`}
      />
    )
  } else if (error) {
    return (
      <div>
        <h1>Error: please try again</h1>
        <p>{error}</p>
      </div>
    )
  } else if (!clientSecret) {
    return <div>Loading payment form...</div>
  }

  const appearance: Appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: 'var(--blue-dark)',
      colorBackground: '#ffffff',
      colorText: '#30313d',
    },
  }

  const options = {
    clientSecret,
    appearance,
  }

  return (
    <PromoCodeProvider plan={selectedPlan}>
      <OnboardingContainer>
        <ChakraProvider>
          <div className="payment mt-6 mt-sm-8 px-4 px-sm-8">
            <Elements stripe={stripePromise} options={options}>
              <CheckoutForm isDirectFlow={isDirectFlow} />
            </Elements>
          </div>
        </ChakraProvider>
      </OnboardingContainer>
    </PromoCodeProvider>
  )
}

export default Payment
